import { SearchCandidateVectorCandidateProfile } from 'modules/candidate-profile/types';
import {
  CustomFieldDisplay,
  CustomFieldType,
  OrganizationCustomFieldConfig
} from 'generated-graphql/hooks/apollo-hooks';

export type CandidateSortDirection = 'asc' | 'desc';

// Expected salary and last applied are not here yet: /sourcing_search returns
// them but CandidateSearchProfile does not declare them.
type BuiltInSortField =
  | 'fullName'
  | 'currentCompany'
  | 'currentJobTitle'
  | 'timeInRole'
  | 'yearsOfExperience'
  | 'locationName'
  | 'score';

const CUSTOM_PREFIX = 'custom:';
type CustomFieldSortField = `${typeof CUSTOM_PREFIX}${string}`;

export type CandidateSortField = BuiltInSortField | CustomFieldSortField;

export const toCustomFieldSortField = (
  fieldKey: string
): CustomFieldSortField => `${CUSTOM_PREFIX}${fieldKey}`;

const isCustomFieldSortField = (
  field: CandidateSortField
): field is CustomFieldSortField => field.startsWith(CUSTOM_PREFIX);

export type CandidateSort = {
  field: CandidateSortField;
  direction: CandidateSortDirection;
};

type SortValue = string | number | null;

const text = (value: string | null | undefined): SortValue =>
  value && value.trim() !== '' ? value : null;

const number = (value: number | null | undefined): SortValue =>
  typeof value === 'number' && !Number.isNaN(value) ? value : null;

const date = (value: string | null | undefined): SortValue => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const SORT_VALUES: Record<
  BuiltInSortField,
  (candidate: SearchCandidateVectorCandidateProfile) => SortValue
> = {
  fullName: (candidate) => text(candidate.full_name),
  currentCompany: (candidate) => text(candidate.current_company),
  currentJobTitle: (candidate) => text(candidate.current_job_title),
  // The column shows a duration, so ascending has to mean the shortest time in
  // role. A later start date is less time served, hence the negation.
  timeInRole: (candidate) => {
    const started = date(candidate.current_role_start_date);
    return started === null ? null : -started;
  },
  yearsOfExperience: (candidate) => number(candidate.years_of_experience),
  locationName: (candidate) => text(candidate.location_name),
  score: (candidate) => number(candidate.score)
};

const asText = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' ? value : null;

const customFieldSortValue =
  ({ fieldKey, type, displayAs }: OrganizationCustomFieldConfig) =>
  (candidate: SearchCandidateVectorCandidateProfile): SortValue => {
    const value = candidate.customFields?.[fieldKey];

    if (Array.isArray(value)) return text(value.join(', '));
    if (typeof value === 'boolean') return Number(value);
    if (type === CustomFieldType.Number) return number(asNumber(value));
    if (type === CustomFieldType.Date) {
      const parsed = date(asText(value));
      // An age counts up as the date goes back, so ascending has to put the
      // youngest first.
      return parsed !== null && displayAs === CustomFieldDisplay.Age
        ? -parsed
        : parsed;
    }

    return text(asText(value));
  };

const resolveSortValue = (
  field: CandidateSortField,
  customFieldConfigs: OrganizationCustomFieldConfig[]
): ((candidate: SearchCandidateVectorCandidateProfile) => SortValue) | null => {
  if (!isCustomFieldSortField(field)) return SORT_VALUES[field];

  const config = customFieldConfigs.find(
    (each) => toCustomFieldSortField(each.fieldKey) === field
  );

  return config ? customFieldSortValue(config) : null;
};

// Orders the loaded page, not every match — the count above the list reports the
// full total. Ordering the whole set needs offset on the sourcing endpoint.
export const sortCandidateProfiles = (
  candidates: SearchCandidateVectorCandidateProfile[],
  sort: CandidateSort | null,
  customFieldConfigs: OrganizationCustomFieldConfig[] = []
): SearchCandidateVectorCandidateProfile[] => {
  if (!sort) return candidates;

  const getValue = resolveSortValue(sort.field, customFieldConfigs);
  if (!getValue) return candidates;
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...candidates].sort((left, right) => {
    const leftValue = getValue(left);
    const rightValue = getValue(right);

    // Blanks last in both directions.
    if (leftValue === null && rightValue === null) return 0;
    if (leftValue === null) return 1;
    if (rightValue === null) return -1;

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * factor;
    }

    return (
      String(leftValue).localeCompare(String(rightValue), undefined, {
        sensitivity: 'base'
      }) * factor
    );
  });
};
